import {
  Component,
  HostListener,
  ElementRef,
  Injector,
  Input,
  Output,
  EventEmitter,
  OnInit,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  AbstractControl,
  ValidationErrors,
  NgControl,
} from '@angular/forms';

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isStart?: boolean;
  isEnd?: boolean;
  isInRange?: boolean;
  isPast?: boolean;
}

export interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}
export interface PannelDatePosition {
  postion: string;
  value: string;
}
@Component({
  selector: 'app-date-range-selector',
  templateUrl: './date-range-selector.component.html',
  styleUrls: ['./date-range-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateRangeSelectorComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DateRangeSelectorComponent),
      multi: true,
    },
  ],
})
export class DateRangeSelectorComponent
  implements ControlValueAccessor, Validator, OnInit
{
  @Input() required = false;

  @Output() datesSelected = new EventEmitter<DateRange>();
  @Input() customStyle: object = { right: '0' };
  startDate: Date | null = null;
  endDate: Date | null = null;
  oneWay = false;
  isOpen = false;
  isTouched = false;

  today = new Date();
  weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  currentMonth = new Date(this.today.getFullYear(), this.today.getMonth(), 1);
  nextMonth = new Date(this.today.getFullYear(), this.today.getMonth() + 1, 1);

  calendarLeft: CalendarDay[] = [];
  calendarRight: CalendarDay[] = [];

  // Résolution différée via Injector (évite NG0200)
  private ngControl: NgControl | null = null;
  private onValidatorChange: () => void = () => {};

  constructor(
    private eRef: ElementRef,
    private injector: Injector,
  ) {
    this.generateCalendars();
  }

  ngOnInit() {
    try {
      this.ngControl = this.injector.get(NgControl);
    } catch {
      this.ngControl = null;
    }
  }

  // ── Validation ───────────────────────────────────────────

  validate(control: AbstractControl): ValidationErrors | null {
    if (!this.required) return null;

    // En mode aller-simple : startDate suffit
    if (this.oneWay) {
      return this.startDate ? null : { required: true };
    }
    // En mode aller-retour : les deux dates sont requises
    return this.startDate && this.endDate ? null : { required: true };
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  /** Affiche l'erreur si touché ET invalide */
  get showError(): boolean {
    if (!this.required) return false;
    const ctrl = this.ngControl?.control;
    if (ctrl) return ctrl.touched && ctrl.invalid;
    return this.isTouched && !this.isValid;
  }

  get errorMessage(): string {
    if (!this.showError) return '';
    if (this.oneWay) return 'Please select a departure date';
    if (!this.startDate) return 'Please select a departure date';
    if (!this.endDate) return 'Please select a return date';
    return '';
  }

  private get isValid(): boolean {
    if (this.oneWay) return !!this.startDate;
    return !!this.startDate && !!this.endDate;
  }

  // ── ControlValueAccessor ─────────────────────────────────

  private onChange: (v: DateRange) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(val: DateRange): void {
    if (!val) return;
    this.startDate = val.startDate ?? null;
    this.endDate = val.endDate ?? null;
    this.generateCalendars();
  }

  registerOnChange(fn: (v: DateRange) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /** Normalise une Date en midi heure locale pour éviter le décalage UTC */
  private normalizeDate(date: Date | null): Date | null {
    if (!date) return null;
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      12,
      0,
      0,
    );
  }

  private emitValue(): void {
    const range: DateRange = {
      startDate: this.normalizeDate(this.startDate),
      endDate: this.normalizeDate(this.endDate),
    };
    this.onChange(range);
    this.datesSelected.emit(range);
    this.onValidatorChange();
  }

  // ── Open / Close ─────────────────────────────────────────

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }

  openPicker() {
    this.isOpen = true;
  }

  closePicker() {
    this.isOpen = false;
    this.isTouched = true;
    this.onTouched();
  }

  // ── Clear ────────────────────────────────────────────────

  clearDates() {
    this.startDate = null;
    this.endDate = null;
    this.generateCalendars();
    this.emitValue();
  }

  // ── Calendar generation ──────────────────────────────────

  generateCalendars() {
    this.calendarLeft = this.generateMonth(this.currentMonth);
    this.calendarRight = this.generateMonth(this.nextMonth);
  }

  generateMonth(month: Date): CalendarDay[] {
    const days: CalendarDay[] = [];
    const year = month.getFullYear();
    const mon = month.getMonth();
    const todayNorm = new Date(
      this.today.getFullYear(),
      this.today.getMonth(),
      this.today.getDate(),
    );

    let firstDay = new Date(year, mon, 1).getDay();
    firstDay = firstDay === 0 ? 6 : firstDay - 1;

    const totalDays = new Date(year, mon + 1, 0).getDate();

    for (let i = 0; i < firstDay; i++) {
      days.push({
        date: new Date(year, mon, i - firstDay + 1),
        isCurrentMonth: false,
      });
    }

    for (let i = 1; i <= totalDays; i++) {
      const date = new Date(year, mon, i);
      days.push({
        date,
        isCurrentMonth: true,
        isPast: date < todayNorm,
        isStart: this.startDate ? this.isSameDate(date, this.startDate) : false,
        isEnd: this.endDate ? this.isSameDate(date, this.endDate) : false,
        isInRange:
          this.startDate && this.endDate
            ? date > this.startDate && date < this.endDate
            : false,
      });
    }
    return days;
  }

  isSameDate(d1: Date, d2: Date): boolean {
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  }

  // ── Date selection ───────────────────────────────────────

  selectDate(day: CalendarDay) {
    if (!day.isCurrentMonth || day.isPast) return;

    if (this.oneWay) {
      this.startDate = day.date;
      this.endDate = null;
      this.isOpen = false;
      this.isTouched = true;
      this.onTouched();
    } else {
      if (!this.startDate || (this.startDate && this.endDate)) {
        this.startDate = day.date;
        this.endDate = null;
      } else {
        if (day.date < this.startDate) {
          this.endDate = this.startDate;
          this.startDate = day.date;
        } else {
          this.endDate = day.date;
        }
        this.isTouched = true;
        this.onTouched();
      }
    }
    this.generateCalendars();
    this.emitValue();
  }

  // ── Navigation ───────────────────────────────────────────

  prevMonth() {
    this.currentMonth = new Date(
      this.currentMonth.getFullYear(),
      this.currentMonth.getMonth() - 1,
      1,
    );
    this.nextMonth = new Date(
      this.currentMonth.getFullYear(),
      this.currentMonth.getMonth() + 1,
      1,
    );
    this.generateCalendars();
  }

  nextMonthClick() {
    this.currentMonth = new Date(
      this.currentMonth.getFullYear(),
      this.currentMonth.getMonth() + 1,
      1,
    );
    this.nextMonth = new Date(
      this.currentMonth.getFullYear(),
      this.currentMonth.getMonth() + 1,
      1,
    );
    this.generateCalendars();
  }

  toggleOneWay() {
    this.oneWay = !this.oneWay;
    if (this.oneWay) {
      this.endDate = null;
      this.generateCalendars();
      this.emitValue();
    }
  }

  // ── Formatted labels ─────────────────────────────────────

  get formattedStart(): string {
    return this.startDate
      ? this.startDate.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      : '';
  }

  get formattedEnd(): string {
    return this.endDate
      ? this.endDate.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      : '';
  }

  getMonthName(date: Date): string {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }
}
